import HeartWithHands from "@assets/icons/heart-with-hands.svg";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { applyCountryCodeFromLang } from "@core/i18n/locale-helper";
import { useNavigate } from "@core/routing/use-navigate";
import { useQueryParams } from "@core/routing/use-query-params";
import { useStateParams } from "@core/routing/use-state-params";
import { countryManager, registerManager } from "@core/service-container";
import styled from "@emotion/styled";
import { AppLogo } from "@ui/components/common/app-logo";
import { BackgroundIcon } from "@ui/components/common/background-icon";
import { Button } from "@ui/components/buttons/button";
import { RoutesPath } from "@ui/navigation/routes";
import { sendAnalyticsEventAccountDeleted } from "@ui/pages/settings/settings-analytics";
import { stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import { useObservable } from "micro-observables";
import React, { useCallback, useEffect } from "react";

export const AccountDeletedPage: React.FC = () => {
	const { leavingReasons } = useStateParams<RoutesPath.AccountDeleted>();
	const { lang } = useQueryParams<RoutesPath.AccountDeleted>();
	const navigate = useNavigate();
	const { format } = useI18n();

	const supportedCountries = useObservable(countryManager.supportedCountries);

	const [fetchSupportedCountries] = useAsyncOperation(
		useCallback(() => countryManager.fetchSupportedCountries(), []),
		{},
	);

	useEffect(() => {
		// ensure we have a valid array before sending analytics
		isNotNil(leavingReasons);
		sendAnalyticsEventAccountDeleted(leavingReasons ?? []);

		const shouldFetchCountries = supportedCountries.length === 0;
		if (shouldFetchCountries) {
			fetchSupportedCountries();
		}

		applyCountryCodeFromLang(registerManager, lang, supportedCountries);
	}, [lang, supportedCountries, fetchSupportedCountries, leavingReasons]);

	return (
		<Container>
			<AppLogo style={{ height: 50, position: "absolute" }} />
			<Content>
				<BackgroundIcon iconSvg={HeartWithHands} />
				<p className="p-size-l-bold">{format("accountDeleted.title")}</p>
				<Description className="p-size-m">{format("accountDeleted.description")}</Description>
				<Button label={format("accountDeleted.cta")} onClick={() => navigate(RoutesPath.Login)} />
			</Content>
		</Container>
	);
};

const Container = styled.div`
	${stack(undefined, undefined, "center")}
	padding: 48px;
	height: 100vh;
`;

const Content = styled.div`
	${stack(16, "center", "center")};
	flex: 1;
`;

const Description = styled.p`
	color: ${colors.grey80};
	white-space: pre-line;
	text-align: center;
`;
